import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  Renderer2,
  input,
  signal,
  inject,
  contentChild,
  viewChild
} from '@angular/core';

import { CommonModule, NgClass } from '@angular/common';
import { KeyCode } from '../../domain/key-code.enum';
import { PipesModule } from '../../pipes/pipes.module';
import { NumberPipe } from '../../pipes/public-api';
import { CurrencyPipe } from '../../pipes/currency.pipe';

import { NumberService } from '../../services/number/number.service';
import { RangeInputDirective } from './range-slider.directive';
import type { InputSignal, Signal, WritableSignal } from '@angular/core';
import type { CurrencyPipeParameters } from '../../pipes/currency.pipe';
import type { FormatParameters } from '../input/input.domain';

enum ValueAction {
  DECREASE = 1,
  INCREASE = -1
}

/**
 * Wrapper component for an <input type="range">
 * Implements the Emerald standards visual design while being heavily influenced by the number spinner code.
 *
 * @example:
 * <form #nsForm="ngForm">
 *   <td-wb-range-slider>
 *       <input tdWbInput
 *         name="rangeSlider"
 *         min="0"
 *         max="100"
 *         step="10"
 *         [disabled]="false"
 *         [(ngModel)]="value">
 *   </td-wb-range-slider>
 * </form>
 * */
@Component({
  selector: 'td-wb-range-slider',
  templateUrl: 'range-slider.component.html',
  styleUrls: ['range-slider.component.scss'],
  imports: [CommonModule, NgClass, PipesModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut()',
    '(keydown)': 'onKeyDownHandler($event)',
    '(wheel)': 'onWheelHandler($event)'
  }
})
export class RangeSliderComponent {

  private input: Signal<RangeInputDirective | undefined> = contentChild(RangeInputDirective);

  readonly valueIndicator: Signal<ElementRef | undefined> = viewChild<ElementRef>('valueIndicator');

  // UI state
  hover: WritableSignal<boolean> = signal<boolean>(false);
  focused: WritableSignal<boolean> = signal<boolean>(false);
  visibility: boolean = false;

  // Inputs as signals
  value: InputSignal<number> = input.required<number>();
  min: InputSignal<number> = input.required<number>();
  max: InputSignal<number> = input.required<number>();
  step: InputSignal<number> = input.required<number>();
  labelPosition: InputSignal<'top' | 'bottom' | 'none'> = input<'top' | 'bottom' | 'none'>('bottom');
  format: InputSignal<'number' | 'currency'> = input<'number' | 'currency'>('currency');
  formatParameters: InputSignal<FormatParameters | undefined> = input<FormatParameters | undefined>(undefined);
  showLabels: InputSignal<boolean> = input<boolean>(true);

  readonly renderer: Renderer2 = inject(Renderer2);
  readonly numberPipe: NumberPipe = inject(NumberPipe);
  readonly currencyPipe: CurrencyPipe = inject(CurrencyPipe);

  isDisabled(): boolean {
    const inputInstance = this.input();
    return inputInstance?.disabled ?? false;
  }

  getFormatted(value: number): string | undefined {
    const fmt = this.format();
    const fmtParams = this.formatParameters();
    if (fmt === undefined) {
      return value.toString();
    } else {
      if (fmt === 'currency') {
        return this.currencyPipe.transform(value, fmtParams as CurrencyPipeParameters);
      }
      if (fmt === 'number') {
        return this.numberPipe.transform(value, fmtParams || { format: 'n0' });
      }
    }
  }

  onMouseEnter(): void {
    this.hover.set(true);
    const inputInstance = this.input();
    inputInstance?.setHoverState(true);
    const valueIndicatorRef = this.valueIndicator();
    if(valueIndicatorRef) {
      this.renderer.setStyle(valueIndicatorRef.nativeElement, 'display', 'block');
    }
  }

  onMouseLeave(): void {
    this.hover.set(false);
    const inputInstance = this.input();
    inputInstance?.setHoverState(false);
    const valueIndicatorRef = this.valueIndicator();
    if(valueIndicatorRef) {
      this.renderer.setStyle(valueIndicatorRef.nativeElement, 'display', 'none');
    }
  }

  onFocusIn(): void {
    this.focused.set(true);
    const inputInstance = this.input();
    inputInstance?.setFocusState(true);
    const valueIndicatorRef = this.valueIndicator();
    if(valueIndicatorRef) {
      this.renderer.setStyle(valueIndicatorRef.nativeElement, 'display', 'block');
    }
  }

  onFocusOut(): void {
    this.focused.set(false);
    const inputInstance = this.input();
    inputInstance?.setFocusState(false);
    const valueIndicatorRef = this.valueIndicator();
    if(valueIndicatorRef) {
      this.renderer.setStyle(valueIndicatorRef.nativeElement, 'display', 'none');
    }
  }

  /**
   * Handles key up/down - increase or decrease input value
   *
   * @param event
   */
  onKeyDownHandler(event: KeyboardEvent): void {
    if (!this.isDisabled()) {
      const direction = RangeSliderComponent.getKeyDirection(event);
      if (direction !== undefined) {
        event.preventDefault();
        this.updateInputValue(direction);
      }
    }
  }

  /**
   * Handle mouse wheel UP/down - increase or decrease input value
   *
   * @param event
   */
  onWheelHandler(event: WheelEvent): void {
    if (!this.isDisabled()) {
      event.preventDefault();
      this.updateInputValue(RangeSliderComponent.getMouseWheelDirection(event));
    }
  }

  /**
   * Update spinner value
   *
   * @param direction {ValueAction} increase or decrease
   */
  private updateInputValue(direction: ValueAction): void {
    const inputInstance = this.input();
    if (this.isDisabled() || !inputInstance) { return; }
    const raw = inputInstance.getValue();
    const current = typeof raw === 'string' ? Number(raw) : raw;
    const currentValue = Number.isFinite(current) ? current : this.min();
    const nextValue = this.getNextValue(currentValue, direction);
    if (this.isValidValue(nextValue)) {
      inputInstance.setValue(nextValue);
    }
  }

  private getNextValue(currentValue: number, direction: ValueAction): number {
    const step = this.step();
    const precision = Math.max(NumberService.getPrecision(currentValue), NumberService.getPrecision(step));
    const delta = direction === ValueAction.INCREASE ? step : -step;
    let nextValue = NumberService.round(currentValue + delta, precision);
    // Clamp based on computed next value
    if (nextValue > this.max()) { nextValue = this.max(); }
    if (nextValue < this.min()) { nextValue = this.min(); }
    return nextValue;
  }

  /**
   * Checks if value is in the allowed range
   *
   * @param value {number}
   * @returns
   */
  private isValidValue(value: number): boolean {
    return value >= this.min() && value <= this.max();
  }

  private static getMouseWheelDirection(event: WheelEvent): ValueAction {
    return event.deltaY < 0 ? ValueAction.INCREASE : ValueAction.DECREASE;
  }

  private static getKeyDirection(event: KeyboardEvent): ValueAction {
    if (event.key === KeyCode.ARROW_UP) {
      return ValueAction.INCREASE;
    }
    if (event.key === KeyCode.ARROW_DOWN) {
      return ValueAction.DECREASE;
    }
  }

  /**
   * Compute the "for" attribute target for the value label.
   */
  inputForAttr(): string {
    const inputInstance = this.input();
    if (!inputInstance) return '';
    // If id is a signal, call it; otherwise, use as string
    const id = typeof inputInstance.id === 'function' ? inputInstance.id() : inputInstance.id;
    return id ?? '';
  }
}
